<script lang="ts">
	//Hooks
	import { beforeUpdate } from 'svelte';
	//Stores
	import { user_settings } from 'src/stores/account/user';
	import { auth_locked_devices } from 'src/stores/device-runtime/local-auth';
	import { devices } from 'src/stores/lists/devices';
	import { os } from 'src/stores/session/native-bridge';
	import { pin_unlocked_devices } from 'src/stores/device-runtime/pin-unlocked';
	//Services
	import { format } from 'svelte-i18n';
	import { postReq } from '../../../services/http';
	import { hapticFeedback } from '../../../services/native_container_requests_ios';
	//Helpers
	import { real_id_z } from '../../../helpers/interface_helpers';
	import { unlockPin } from '../../../helpers/pin_lock_helpers';
	//Design system components
	import {
		DeviceButton,
		DeviceButtonV2,
		DoubleDeviceButton,
		DoubleDeviceButtonV2,
		toast,
		type DeviceButtonV2PropsTypes,
		type DoubleDeviceButtonV2PropsTypes,
	} from '@Shelly/shelly-design-system';
	//Components
	import PinUnlock from '../../PinUnlock.svelte';
	import { hasControlRights } from 'src/helpers/user_helpers';

	let className = '';
	export { className as class };

	export let device, status;
	export let devBtnsV2Props: {
		deviceButtonV2?: Partial<DeviceButtonV2PropsTypes>;
		doubleDeviceButtonV2?: Partial<DoubleDeviceButtonV2PropsTypes>;
	} = {
		deviceButtonV2: { size: 's' },
		doubleDeviceButtonV2: { size: 's' },
	};
	export let useV2Components = false;

	let isLoading = false;

	let direction;

	let pinLocked;
	let showModal = false;

	let callbackValue;

	let btn1 = false;
	let btn2 = false;

	$: hasRights = hasControlRights(device);

	const checkStates = () => {
		if (status['Meter:1']['value.Electric_W_Consumed/66049'] !== 0) {
			direction = 'stop';
		}
		if (typeof direction === 'undefined') {
			btn1 = false;
			btn2 = false;
		}
		if (status['Meter:1']['value.Electric_W_Consumed/66049'] === 0 && direction === 'stop') {
			direction = undefined;
		}
	};

	beforeUpdate(() => {
		pinLocked =
			typeof $pin_unlocked_devices[device.id] === 'undefined' && $devices[device.id]?.pin_lock;
		if (typeof status !== 'undefined') {
			checkStates();
		}
	});

	const handleDirectionRequest = async (dir) => {
		isLoading = true;
		if (typeof direction === 'undefined') {
			direction = dir === 1 ? 'open' : 'close';
			if (direction === 'open') {
				btn1 = true;
				btn2 = false;
			} else if (direction === 'close') {
				btn2 = true;
				btn1 = false;
			}
		} else {
			direction = 'stop';
		}
		if (direction === 'stop') {
			btn1 = false;
			btn2 = false;
		}

		const res = await postReq(`/device/relay/roller/control`, {
			id: real_id_z(device.id),
			channel: device.channel,
			direction: direction,
		});

		if (res.isok) {
			if (direction === 'stop') {
				direction = undefined;
			}
		} else if ($auth_locked_devices.indexOf(real_id_z(device.id)) == -1) {
			toast.open({ type: 'danger', title: $format('error_global') });
		}
		// if (!res?.isok) {
		setTimeout(() => {
			if (status['Meter:1']['value.Electric_W_Consumed/66049'] === 0) {
				direction = undefined;
			}
		}, 5000); //needed for faking direction control TODO: remove after firmware update with good statuses
		// }
		setTimeout(() => {
			isLoading = false;
		}, 100); //need this delay to prevent flickering
	};

	const handleRequest = (dir) => {
		if (!pinLocked) {
			if ($os === 'IOS') {
				hapticFeedback('buttonPressed');
			}
			handleDirectionRequest(dir);
		} else {
			callbackValue = dir;
			showModal = true;
		}
	};

	const handlePin = () => {
		pinLocked = unlockPin(device, pinLocked);
		handleRequest(callbackValue);
		callbackValue = undefined;
	};
</script>

{#if useV2Components}
	<DoubleDeviceButtonV2
		id="doubledevicebutton-fic5qrl"
		aria-label={$format('label_direction', { default: 'Direction' })}
		isActive={btn1 || btn2}
		{isLoading}
		class={className}
		disabled={!hasRights}
		{...devBtnsV2Props.doubleDeviceButtonV2}
	>
		<DeviceButtonV2
			id="devicebutton-6etz4wr"
			slot="buttonLeft"
			icon={btn1 ? 'fa fa-pause' : 'fa fa-arrow-up'}
			isActive={false}
			aria-pressed={btn1}
			aria-label={$format('label_direction_up_or_pause', { default: 'Direction Up or Pause' })}
			on:click={(e) => {
				e.stopPropagation();
				handleRequest(1);
			}}
			{...devBtnsV2Props.deviceButtonV2}
		/>
		<DeviceButtonV2
			id="devicebutton-eve5fk1"
			slot="buttonRight"
			icon={btn2 ? 'fa fa-pause' : 'fa fa-arrow-down'}
			isActive={false}
			aria-pressed={btn2}
			aria-label={$format('label_direction_down_or_pause', { default: 'Direction Down or Pause' })}
			on:click={(e) => {
				e.stopPropagation();
				handleRequest(0);
			}}
			{...devBtnsV2Props.deviceButtonV2}
		/>
	</DoubleDeviceButtonV2>
{:else}
	<DoubleDeviceButton
		id="doubledevicebutton-fic5qrl"
		aria-label={$format('label_direction', { default: 'Direction' })}
		isActive={btn1 || btn2}
		{isLoading}
		class={className}
		disabled={!hasRights}
	>
		<DeviceButton
			id="devicebutton-6etz4wr"
			slot="buttonLeft"
			icon={btn1 ? 'fa fa-pause' : 'fa fa-arrow-up'}
			aria-pressed={btn1}
			aria-label={$format('label_direction_up_or_pause', { default: 'Direction Up or Pause' })}
			size="l"
			on:click={(e) => {
				e.stopPropagation();
				handleRequest(1);
			}}
		/>
		<DeviceButton
			id="devicebutton-eve5fk1"
			slot="buttonRight"
			icon={btn2 ? 'fa fa-pause' : 'fa fa-arrow-down'}
			aria-pressed={btn2}
			aria-label={$format('label_direction_down_or_pause', { default: 'Direction Down or Pause' })}
			size="l"
			on:click={(e) => {
				e.stopPropagation();
				handleRequest(0);
			}}
		/>
	</DoubleDeviceButton>
{/if}

{#if showModal}
	<PinUnlock bind:showModal on:unlock={handlePin} on:cancel={() => (showModal = false)} />
{/if}
